import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import uniq from "lodash/uniq";
import { styled } from "@mui/material/styles";
import { IconButton, MenuItem, TextField, Typography, Button, Tooltip } from "@mui/material";

import { OpenInNewWindow, Delete } from "orion-components/CBComponents/Icons";
import { Dialog } from "orion-components/CBComponents";
import ResizableGrid from "./components/ResizableGrid";
import { getDir } from "orion-components/i18n/Config/selector";
import { subscribeCameras } from "orion-components/GlobalData/Actions";
import { updateContextPropertyWithDotNotation } from "../../../ContextualData/Actions";
import { generateDirStyles } from "orion-components/SharedComponents/GenerateDirStyles/GenerateDirStyles";
import { getWidgetState } from "orion-components/Profiles/Selectors";
import { Translate, getTranslation } from "orion-components/i18n";
import WidgetMenu from "./components/WidgetMenu";
import { getGlobalWidgetState } from "../Selectors";
import { BaseWidget } from "../shared";

const CustomizedTextField = styled(TextField)(({ height }) => ({
	"& .MuiOutlinedInput-root": {
		"& fieldset": {
			borderColor: "#5E6571"
		},
		"&:hover fieldset": {
			borderColor: "#4DB5F4"
		},
		"&.Mui-focused fieldset": {
			borderColor: "#4DB5F4"
		},
		"&.Mui-disabled fieldset": {
			borderColor: "#5E657180"
		},
		"& .MuiInputBase-input": {
			fontSize: "11px",
			color: "#FFFFFF", // Text color
			textAlign: "left",
			fontWeight: "normal",
			fontFamily: "Roboto, sans-serif",
			letterSpacing: "0px",
			opacity: 1,
			height: height,
			paddingTop: "25px"
		},
		"& .MuiInputBase-input::placeholder": {
			color: "#FFFFFF", // Placeholder color
			opacity: 1 // Ensure full opacity for visibility
		},
		"& .MuiSelect-icon": {
			color: "#FFFFFF"
		}
	},
	"& .MuiInputBase-root": {
		height: height
	}
}));

const baseStyles = {
	clustersContainer: {
		marginTop: 10,
		marginBottom: 28,
		width: "97.5%",
		height: "auto",
		marginLeft: 10
	},
	clustersControlsSection: { display: "flex", width: "100%" },
	clustersDropDownControl1: {
		marginRight: "3.3%",
		width: "30.75%",
		fontSize: 12
	},
	clustersDropDownControl2: {
		width: "30.75%",
		fontSize: 12,
		marginLeft: 2,
		display: "flex",
		alignItems: "center"
	},
	dropDownMenuPaperProps: {
		backgroundColor: "black",
		color: "white",
		marginTop: "-30px",
		fontSize: 12
	},
	menuItem: { fontSize: "1em" },
	clustersButtonWrapper: {
		justifyContent: "right",
		display: "flex",
		alignItems: "center"
	},
	moreButton: { width: "2.64%", height: "0.1%" },
	OpenInNewWindowIcon: {
		margin: "12px",
		padding: 0
	},
	clustersGridSection: {
		width: "100%"
	},
	emptyContainer: { width: "60%", margin: "0 10px", display: "flex" },
	header: {
		color: "#FFFFFF",
		fontFamily: "Roboto",
		fontSize: 14
	},
	note: {
		color: "#fff",
		fontFamily: "Roboto",
		marginTop: "8px",
		opacity: "0.69"
	},
	setDefaultBtn: {
		fontSize: 10,
		padding: "unset"
	},
	removeBtn: {
		cursor: "pointer"
	}
};
const initialState = { rows: 2, columns: 2, cameras: [] };
const CameraClusters = (props) => {
	const { id, isOnAlertProfile, streamVideoWallClusterCameras, contextId, selectedDateTime, readOnly, updateEntity } = props;
	const getStyles = dir => generateDirStyles(baseStyles, dir);
	const allCameraClusters = useSelector(state => state.globalData.cameraClusters);
	const entity = useSelector(state => state?.contextualData?.[contextId]?.entity);
	const targetClustersData = entity?.activity?.target?.cameraClusters;
	const cameraClustersData = entity?.cameraClusters;
	const mergedClusters = {
		clusters: uniq([...(targetClustersData?.clusters ?? []), ...(cameraClustersData?.clusters ?? [])]),
		defaultClusterId: targetClustersData?.defaultClusterId || cameraClustersData?.defaultClusterId
	};
	const cameraClusters = mergedClusters?.clusters ?? [];
	const defaultClusterId = mergedClusters?.defaultClusterId;
	const enabled = useSelector((state) => getWidgetState(state)(id, "enabled") || props.enabled);
	const widgetState = useSelector((state) => getGlobalWidgetState(state)(id)) || {};
	const expandedState = widgetState?.autoExpand;

	const [sortedClusters, setSortedClusters] = useState([]);
	// const [dateTime, setDateTime] = useState("default");
	const [selectedClusterId, setSelectedClusterId] = useState(null);
	const [clusterData, setClusterData] = useState(initialState); // Combined state
	const dir = useSelector(state => getDir(state));
	const [styles, setStyles] = useState({});
	const [renderClusters, setRenderClusters] = useState(false);
	const [dialog, setDialog] = useState("");

	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(subscribeCameras());
	}, []);

	useEffect(() => {
		setStyles(getStyles(dir)); // Update styles when 'dir' changes
	}, [dir]);

	useEffect(() => {
		// Cloning clusters before sorting to avoid mutating the original array.
		const clusters = mergedClusters?.clusters?.length ? [...mergedClusters?.clusters] : [];
		let defaultClusterId = selectedClusterId !== null ? selectedClusterId : mergedClusters?.defaultClusterId;

		if (clusters?.length > 0 && allCameraClusters) {
			if (clusters?.length === 1 && !allCameraClusters[defaultClusterId]) {
				setRenderClusters(false);
			} else {
				if (!allCameraClusters[defaultClusterId]) {
					setRenderClusters(false);

					for (const item of clusters || []) {
						if (allCameraClusters[item]) {
							defaultClusterId = item;
							break; // Exit loop as soon as the condition is met
						}
					}
				} else {
					const defaultClusterIndex = clusters.findIndex(id => id === defaultClusterId);

					if (defaultClusterIndex !== -1) {
						// Remove the default cluster and store it
						const [defaultCluster] = clusters.splice(defaultClusterIndex, 1);
						// Add the default cluster to the front
						clusters.unshift(defaultCluster);
					}
					// Update the state with the sorted clusters
					const sorted = [...clusters];

					setSortedClusters(sorted);
					setClusterData({
						id: allCameraClusters[sorted[0]]?.id ?? 0,
						rows: allCameraClusters[sorted[0]]?.rows ?? 2,
						columns: allCameraClusters[sorted[0]]?.columns ?? 2,
						cameras: allCameraClusters[sorted[0]]?.cameras ?? []
					});
					streamVideoWallClusterCameras(allCameraClusters[sorted[0]]?.cameras ?? []);
					setSelectedClusterId(sorted[0]);
					setRenderClusters(true);
				}
			}
		} else {
			setRenderClusters(false);
			setClusterData(initialState);
			setSelectedClusterId(null);
			setSortedClusters([]);
		}
	}, [targetClustersData, cameraClustersData, allCameraClusters, selectedClusterId]);

	const onClusterChange = e => {
		setSelectedClusterId(e.target.value);
		const getSelectedCluster = sortedClusters.filter(id => id === e.target.value)[0];

		setClusterData({
			id: e.target.value,
			rows: allCameraClusters[getSelectedCluster].rows,
			columns: allCameraClusters[getSelectedCluster].columns,
			cameras: allCameraClusters[getSelectedCluster].cameras
		});

		if (isOnAlertProfile) {
			dispatch(
				updateContextPropertyWithDotNotation(
					contextId,
					"entity.cameraClusters.selectedCameraClusterId",
					e.target.value
				)
			);
		}
	};

	const openClusterInCameraWall = e => {
		const url = `/camera-wall-app/#/camera-cluster/${selectedClusterId}/`;
		if (e.ctrlKey) {
			window.open(url, "_blank");
		} else {
			window.location.replace(url);
		}
	};

	const addCameraClustersToEntity = (clusterIds) => {
		if (contextId && clusterIds?.length > 0) {
			const update = {
				cameraClusters: {
					clusters: uniq([...cameraClusters, ...clusterIds]),
					defaultClusterId: defaultClusterId || clusterIds[0]
				}
			};
			dispatch(updateEntity(contextId, update, false));
		}
	};

	const setDefaultCluster = (defaultClusterId) => {
		if (contextId) {
			const update = {
				cameraClusters: {
					clusters: cameraClusters,
					defaultClusterId
				}
			};
			dispatch(updateEntity(contextId, update, false))
		};
	};

	const removeCluster = () => {
		const clusters = cameraClusters?.filter((id) => id !== selectedClusterId);
		const update = {
			cameraClusters: {
				clusters,
				defaultClusterId: (clusters?.length && defaultClusterId !== selectedClusterId) ? defaultClusterId : clusters[0] || null
			}
		};

		dispatch(updateEntity(contextId, update, false));
		setDialog("");
	};

	const getWidgetMenu = () => {
		return <WidgetMenu
			widgetName={id}
			widgetState={widgetState}
			expandedState={expandedState}
			dir={dir}
			addCameraClustersToEntity={addCameraClustersToEntity}
			cameraClusters={cameraClusters}
		/>;
	};

	const generateClusterGrid = () => {
		if (renderClusters) {
			return (
				<div style={styles.clustersContainer}>
					<div style={styles.clustersControlsSection}>
						<div style={styles.clustersDropDownControl2}>
							<CustomizedTextField
								variant="outlined"
								name="cluster"
								value={selectedClusterId}
								onChange={onClusterChange}
								fullWidth
								height={30}
								select
								SelectProps={{
									MenuProps: {
										sx: {
											zIndex: "2300 !important"
										},
										getContentAnchorEl: null, // Prevents default anchoring behavior
										anchorOrigin: {
											vertical: "bottom",
											horizontal: "left"
										},
										transformOrigin: {
											vertical: "top",
											horizontal: "left"
										},
										PaperProps: {
											sx: styles.dropDownMenuPaperProps
										}
									}
								}}
							>
								{sortedClusters.map((id, index) => {
									// Deleted camera clusters are handled here.
									if (allCameraClusters[id]) {
										const item = allCameraClusters[id];
										return (
											<MenuItem value={item.id} key={index} sx={styles.menuItem}>
												{item.name}
											</MenuItem>
										);
									}

									return <></>;
								})}
							</CustomizedTextField>
						</div>
						<div style={styles.emptyContainer}>
							{!readOnly && cameraClusters?.length > 1 && selectedClusterId && defaultClusterId !== selectedClusterId &&
								<Button style={styles.setDefaultBtn} onClick={() => setDefaultCluster(selectedClusterId)}>
									<Translate value={"global.profiles.widgets.cameraClusters.setDefault"} />
								</Button>
							}
						</div>
						<div style={{ display: "flex", alignItems: "center" }}>
							{!readOnly && cameraClusters?.length > 0 && selectedClusterId && <Tooltip
								title={getTranslation(
									"global.profiles.widgets.cameraClusters.removeCluster"
								)}
							>
								<div style={styles.removeBtn} onClick={() => setDialog("remove-camera-cluster-dialog")}>
									<Delete />
								</div>
							</Tooltip>}
						</div>
						<div style={styles.clustersButtonWrapper}>
							<IconButton style={styles.OpenInNewWindowIcon} onClick={openClusterInCameraWall}>
								<OpenInNewWindow />
							</IconButton>
						</div>
					</div>
					<div style={styles.clustersGridSection}>
						<ResizableGrid
							key={`${clusterData.id}_${selectedDateTime}`}
							rows={clusterData.rows}
							columns={clusterData.columns}
							cameras={clusterData.cameras}
							isOnAlertProfile={isOnAlertProfile}
							alertDateTime={selectedDateTime}
						/>
					</div>
					{dialog === "remove-camera-cluster-dialog" && <Dialog
						open={dialog === "remove-camera-cluster-dialog"}
						confirm={{
							label: getTranslation("global.profiles.widgets.cameraClusters.remove"),
							action: removeCluster,
							style: { color: "#5594B9" }
						}}
						abort={{
							label: getTranslation("global.profiles.widgets.cameraClusters.cancel"),
							action: () => setDialog(""),
							style: "#B4B9BF"
						}}
						dir={dir}
						paperPropStyles={{
							width: "607px",
							padding: "1.5% 0.75%"
						}}
					>
						<div>
							<Typography fontSize="16px" style={baseStyles.header}>
								<Translate
									value={"global.profiles.widgets.cameraClusters.removeCluster"}
								/>
							</Typography>

							<Typography fontSize="11px" style={baseStyles.note}>
								<Translate value={"global.profiles.widgets.cameraClusters.note"} />
							</Typography>
						</div>
					</Dialog>}
				</div >
			);
		} else {
			return <>
				<Typography style={{ margin: "12px auto", color: "#fff" }} align="center" variant="caption">
					<Translate value={"global.profiles.widgets.cameraClusters.noClusters"} />
				</Typography>
			</>;
		}
	};

	return isOnAlertProfile ? (
		<>
			{generateClusterGrid()}
		</>
	) : (
		<BaseWidget
			enabled={enabled}
			title={getTranslation("global.profiles.widgets.cameraClusters.title")}
			dir={dir}
			widgetMenu={!readOnly && getWidgetMenu()}
		>
			{generateClusterGrid()}
		</BaseWidget>
	)
};

CameraClusters.propTypes = {
	cameraClusters: PropTypes.object,
	isOnAlertProfile: PropTypes.bool,
	streamVideoWallClusterCameras: PropTypes.func,
	alertDateTime: PropTypes.string,
	selectedDateTime: PropTypes.string,
	contextId: PropTypes.string,
	updateEntity: PropTypes.func
};

CameraClusters.defaultProps = {
	updateEntity: () => { }
};

const areEqual = (prevProps, nextProps) => {
	return prevProps.contextId === nextProps.contextId && prevProps.selectedDateTime === nextProps.selectedDateTime && prevProps.cameraClusters === nextProps.cameraClusters;
};

export default React.memo(CameraClusters, areEqual);
